<template>
  <VaSidebar v-model="writableVisible" class="recon-sidebar" :width="sidebarWidth" :color="color" minimized-width="0">
    <div class="sidebar-section-title">ĐIỀU HƯỚNG</div>
    <div class="sidebar-navigation">
      <template v-for="(route, index) in visibleRoutes" :key="route.name">
        <div v-if="route.name === 'management'" class="sidebar-section-title sidebar-section-spaced">QUẢN TRỊ</div>
        <div v-else-if="route.name === 'transaction-import'" class="sidebar-section-title sidebar-section-spaced">GIAO DỊCH</div>
        <VaSidebarItem
          v-if="!route.children"
          class="sidebar-nav-item"
          :to="{ name: route.name }"
          :active="routeHasActiveChild(route)"
          :active-color="activeColor"
          :text-color="textColor(route)"
          :aria-label="`Đi đến ${t(route.displayName)}`"
          hover-opacity="0.10"
        >
          <VaSidebarItemContent class="sidebar-item-content"
            ><VaIcon
              v-if="route.meta.icon"
              aria-hidden="true"
              :name="route.meta.icon"
              size="20px"
              :color="iconColor(route)"
            /><VaSidebarItemTitle class="leading-5 font-semibold">{{
              t(route.displayName)
            }}</VaSidebarItemTitle></VaSidebarItemContent
          >
        </VaSidebarItem>
        <VaCollapse v-else :model-value="value[index]" @update:modelValue="value[index] = $event">
          <template #header="{ value: isCollapsed }">
            <VaSidebarItem
              class="sidebar-nav-item sidebar-group-item"
              :to="route.children ? undefined : { name: route.name }"
              :active="routeHasActiveChild(route)"
              :active-color="activeColor"
              :text-color="textColor(route)"
              :aria-label="`Mở nhóm ${t(route.displayName)}`"
              role="button"
              hover-opacity="0.10"
            >
              <VaSidebarItemContent class="sidebar-item-content">
                <VaIcon
                  v-if="route.meta.icon"
                  aria-hidden="true"
                  :name="route.meta.icon"
                  size="20px"
                  :color="iconColor(route)"
                />
                <VaSidebarItemTitle class="flex justify-between items-center leading-5 font-semibold">
                  {{ t(route.displayName) }}
                  <VaIcon v-if="route.children" :name="arrowDirection(isCollapsed)" size="20px" />
                </VaSidebarItemTitle>
              </VaSidebarItemContent>
            </VaSidebarItem>
          </template>
          <template #body>
            <div v-for="(childRoute, index2) in route.children" :key="index2">
              <VaSidebarItem
                class="sidebar-nav-item sidebar-child-item"
                :to="{ name: childRoute.name }"
                :active="isActiveChildRoute(childRoute)"
                :active-color="activeColor"
                :text-color="textColor(childRoute)"
                :aria-label="`Đi đến ${t(childRoute.displayName)}`"
                hover-opacity="0.10"
              >
                <VaSidebarItemContent class="sidebar-item-content">
                  <VaSidebarItemTitle class="leading-5 font-semibold">
                    {{ t(childRoute.displayName) }}
                  </VaSidebarItemTitle>
                </VaSidebarItemContent>
              </VaSidebarItem>
            </div>
          </template>
        </VaCollapse>
      </template>
    </div>
  </VaSidebar>
</template>
<script lang="ts">
import { defineComponent, watch, ref, computed } from 'vue'
import { useRoute } from 'vue-router'

import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'
import { useAuthStore } from '../../stores/auth'

import navigationRoutes, { type INavigationRoute } from './NavigationRoutes'

export default defineComponent({
  name: 'Sidebar',
  props: {
    visible: { type: Boolean, default: true },
    mobile: { type: Boolean, default: false },
  },
  emits: ['update:visible'],

  setup: (props, { emit }) => {
    const { getColor, colorToRgba } = useColors()
    const route = useRoute()
    const { t } = useI18n()
    const auth = useAuthStore()
    const visibleRoutes = computed(() => navigationRoutes.routes.flatMap((item) => {
      if (item.name === 'system-documents') return []
      const children = item.children?.filter((child) => !child.permission || auth.user?.effective_permissions?.includes(child.permission))
      if (item.permission && !auth.user?.effective_permissions?.includes(item.permission)) return []
      if (item.children && !children?.length) return []
      return [{ ...item, ...(item.children ? { children } : {}) }]
    }))

    const value = ref<boolean[]>([])

    const writableVisible = computed({
      get: () => props.visible,
      set: (v: boolean) => emit('update:visible', v),
    })

    const isActiveChildRoute = (child: INavigationRoute) => route.name === child.name

    const routeHasActiveChild = (section: INavigationRoute) => {
      if (!section.children) {
        return route.name === section.name
      }

      return section.children.some(({ name }) => route.name === name)
    }

    const setActiveExpand = () =>
      (value.value = visibleRoutes.value.map((route: INavigationRoute) => routeHasActiveChild(route)))

    const sidebarWidth = computed(() => (props.mobile ? '100vw' : '280px'))
    const color = computed(() => getColor('background-secondary'))
    const activeColor = computed(() => colorToRgba(getColor('focus'), 0.1))

    const iconColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'secondary')
    const textColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'textPrimary')
    const arrowDirection = (state: boolean) => (state ? 'va-arrow-up' : 'va-arrow-down')

    watch(() => route.fullPath, setActiveExpand, { immediate: true })

    return {
      writableVisible,
      sidebarWidth,
      value,
      color,
      activeColor,
      navigationRoutes,
      visibleRoutes,
      routeHasActiveChild,
      isActiveChildRoute,
      t,
      iconColor,
      textColor,
      arrowDirection,
    }
  },
})
</script>
<style scoped>
.recon-sidebar {
  --va-sidebar-item-content-padding: 0.66rem 0.75rem;
  --va-sidebar-item-content-gap: 0.72rem;
  --va-sidebar-item-active-border-size: 0px;
  --va-sidebar-menu-overflow-y: auto;
  --va-sidebar-height: 100vh;
  --va-sidebar-min-height: 100vh;
  border-right: 1px solid var(--va-background-border);
  background: var(--va-background-primary) !important;
}
.recon-sidebar :deep(.va-sidebar__menu) { padding: 1rem 0.75rem; box-sizing: border-box; }
.sidebar-section-title { padding: 0.25rem 0.75rem 0.45rem; color: var(--va-text-secondary); font-size: 0.62rem; font-weight: 800; letter-spacing: 0.11em; }
.sidebar-section-spaced { margin-top: 1.15rem; }
.sidebar-navigation { display: grid; gap: 0.2rem; }
.recon-sidebar :deep(.sidebar-nav-item) { width: 100%; margin: 0; padding: 0; border: 0; border-radius: 9px; color: var(--va-text-primary); transition: background-color 140ms ease, color 140ms ease; }
.recon-sidebar :deep(.sidebar-nav-item:hover) { background: var(--va-background-secondary); }
.recon-sidebar :deep(.sidebar-nav-item--active) { background: color-mix(in srgb, var(--va-primary) 10%, var(--va-background-primary)); color: var(--va-primary); }
.recon-sidebar :deep(.sidebar-item-content) { min-height: 42px; padding: 0.65rem 0.75rem; }
.recon-sidebar :deep(.sidebar-nav-item .va-icon) { flex: 0 0 20px; }
.recon-sidebar :deep(.sidebar-nav-item--active .va-icon) { color: var(--va-primary) !important; }
.recon-sidebar :deep(.sidebar-nav-item .va-sidebar-item-title) { font-size: 0.84rem; font-weight: 620; }
.recon-sidebar :deep(.sidebar-child-item) { margin: 0.08rem 0; }
.recon-sidebar :deep(.sidebar-child-item .sidebar-item-content) { min-height: 37px; padding-left: 2.55rem; }
.recon-sidebar :deep(.sidebar-child-item .va-sidebar-item-title) { font-size: 0.8rem; font-weight: 550; }
.recon-sidebar :deep(.sidebar-group-item .va-sidebar-item-title) { font-weight: 700; }
.recon-sidebar.va-sidebar--minimized .sidebar-section-title { display: none; }
.recon-sidebar.va-sidebar--minimized :deep(.sidebar-item-content) { justify-content: center; padding-inline: 0.5rem; }
@media (max-width: 767px) {
  .recon-sidebar { --va-sidebar-height: 100dvh; --va-sidebar-min-height: 100dvh; }
}
</style>
